import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

/**
 * Get all releases for a feature group
 */
export const getReleases = async (featureGroupId) => {
  try {
    const { data, error } = await supabase
      .from('releases')
      .select('*')
      .eq('feature_group_id', featureGroupId)
      .order('start_date', { ascending: false, nullsLast: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error fetching releases:', error);
    throw error;
  }
};

/**
 * Get a single release by ID
 */
export const getReleaseById = async (releaseId) => {
  try {
    const { data, error } = await supabase
      .from('releases')
      .select('*')
      .eq('id', releaseId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error fetching release:', error);
    throw error;
  }
};

/**
 * Get the current release (where today is between start_date and end_date)
 * If end_date is NULL, treat it as ongoing (no end date)
 */
export const getCurrentRelease = async (featureGroupId) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    
    // Query for releases where:
    // - start_date <= today (release has started)
    // - (end_date IS NULL OR end_date >= today) (release hasn't ended or has no end date)
    const { data, error } = await supabase
      .from('releases')
      .select('*')
      .eq('feature_group_id', featureGroupId)
      .lte('start_date', today)
      .or(`end_date.is.null,end_date.gte.${today}`)
      .order('start_date', { ascending: false })
      .limit(1);

    if (error) throw error;
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    logger.error('Error fetching current release:', error);
    throw error;
  }
};

/**
 * Create a new release
 */
export const createRelease = async (releaseData) => {
  try {
    const { data, error } = await supabase
      .from('releases')
      .insert([{
        feature_group_id: releaseData.feature_group_id,
        name: releaseData.name.trim(),
        internal_description: releaseData.internal_description || null,
        public_description: releaseData.public_description || null,
        start_date: releaseData.start_date || null,
        end_date: releaseData.end_date || null,
        status: releaseData.status || 'draft',
        current_subversion: releaseData.current_subversion || null,
        comments: releaseData.comments || null
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error creating release:', error);
    throw error;
  }
};

/**
 * Update an existing release
 */
export const updateRelease = async (releaseId, releaseData) => {
  try {
    const { data, error } = await supabase
      .from('releases')
      .update({
        name: releaseData.name.trim(),
        internal_description: releaseData.internal_description || null,
        public_description: releaseData.public_description || null,
        start_date: releaseData.start_date || null,
        end_date: releaseData.end_date || null,
        status: releaseData.status || 'draft',
        current_subversion: releaseData.current_subversion || null,
        comments: releaseData.comments || null,
        updated_at: new Date().toISOString()
      })
      .eq('id', releaseId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error updating release:', error);
    throw error;
  }
};

/**
 * Delete a release
 */
export const deleteRelease = async (releaseId) => {
  try {
    const { error } = await supabase
      .from('releases')
      .delete()
      .eq('id', releaseId);

    if (error) throw error;
  } catch (error) {
    logger.error('Error deleting release:', error);
    throw error;
  }
};

/**
 * Get features for a release
 */
export const getReleaseFeatures = async (releaseId) => {
  try {
    const { data, error } = await supabase
      .from('feature_releases')
      .select(`
        feature_id,
        features (
          id,
          name,
          description,
          status,
          feature_group_id,
          release_ready
        )
      `)
      .eq('release_id', releaseId);

    if (error) throw error;
    return (data || []).map(fr => fr.features).filter(Boolean);
  } catch (error) {
    logger.error('Error fetching release features:', error);
    throw error;
  }
};

/**
 * Get releases for a feature
 */
export const getFeatureReleases = async (featureId) => {
  try {
    const { data, error } = await supabase
      .from('feature_releases')
      .select(`
        release_id,
        releases (
          id,
          name,
          status,
          start_date,
          end_date
        )
      `)
      .eq('feature_id', featureId);

    if (error) throw error;
    return (data || []).map(fr => fr.releases).filter(Boolean);
  } catch (error) {
    logger.error('Error fetching feature releases:', error);
    throw error;
  }
};

/**
 * Set releases for a feature (replaces all existing)
 */
export const setFeatureReleases = async (featureId, releaseIds) => {
  try {
    // Delete existing feature releases
    await supabase
      .from('feature_releases')
      .delete()
      .eq('feature_id', featureId);

    // Insert new feature releases
    if (releaseIds && releaseIds.length > 0) {
      const { error } = await supabase
        .from('feature_releases')
        .insert(releaseIds.map(releaseId => ({
          feature_id: featureId,
          release_id: releaseId
        })));

      if (error) throw error;
    }
  } catch (error) {
    logger.error('Error setting feature releases:', error);
    throw error;
  }
};

/**
 * Get release feature count
 */
export const getReleaseFeatureCount = async (releaseId) => {
  try {
    const { count, error } = await supabase
      .from('feature_releases')
      .select('*', { count: 'exact', head: true })
      .eq('release_id', releaseId);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    logger.error('Error getting release feature count:', error);
    throw error;
  }
};

/**
 * Get all feature releases for a feature group (batch query)
 * Returns a Map of featureId -> array of releases
 */
export const getAllFeatureReleasesForGroup = async (featureGroupId) => {
  try {
    // Get all feature_releases for features in this group, with release details
    const { data, error } = await supabase
      .from('feature_releases')
      .select(`
        feature_id,
        releases!inner (
          id,
          name,
          status,
          start_date,
          end_date,
          feature_group_id
        )
      `)
      .eq('releases.feature_group_id', featureGroupId);

    if (error) throw error;

    // Build a Map of featureId -> releases array
    const releasesMap = new Map();
    (data || []).forEach(fr => {
      if (fr.releases) {
        const featureId = fr.feature_id;
        if (!releasesMap.has(featureId)) {
          releasesMap.set(featureId, []);
        }
        releasesMap.get(featureId).push(fr.releases);
      }
    });

    return releasesMap;
  } catch (error) {
    logger.error('Error fetching all feature releases for group:', error);
    throw error;
  }
};

